import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building, MapPin, CreditCard, Lock } from 'lucide-react';
import { venueClaimService, type VenueProfile } from '@/services/venueClaimService';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { CertificationAgreementsModal } from '@/components/legal/CertificationAgreementsModal';
import { useCertificationAgreement } from '@/hooks/useCertificationAgreement';

interface VenueClaimProps {}

export const VenueClaim: React.FC<VenueClaimProps> = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState<VenueProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimProcessStep, setClaimProcessStep] = useState<'info' | 'registration' | 'payment'>('info');

  const { 
    showModal: showCertificationModal, 
    isSubmitting: isCertSubmitting,
    checkAndShowModal,
    submitAgreement,
    closeModal: closeCertificationModal
  } = useCertificationAgreement();

  useEffect(() => {
    if (venueId) {
      loadVenueData();
    }
  }, [venueId]);

  const loadVenueData = async () => {
    try {
      const venues = await venueClaimService.getVenueProfiles();
      const foundVenue = venues.find(v => v.id === venueId);
      setVenue(foundVenue || null);
    } catch (error) {
      console.error('Error loading venue:', error);
      toast.error('Failed to load venue information');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimBusiness = async () => {
    if (!user) {
      toast.error('Please log in to claim this business');
      navigate('/auth');
      return;
    }
    
    // Check if user has accepted certification agreements
    const hasAccepted = await checkAndShowModal('venue');
    if (!hasAccepted) {
      // Modal will be shown, wait for user to accept
      return;
    }
    
    setClaimProcessStep('registration');
  };

  const handleCertificationSubmit = async () => {
    const success = await submitAgreement('venue', true);
    if (success) {
      setClaimProcessStep('registration');
    }
  };

  const handleRegistrationComplete = () => {
    setClaimProcessStep('payment');
  };

  const handlePaymentComplete = () => {
    toast.success('Payment successful! Your venue claim is being processed.');
    navigate('/venue-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading venue information...</p>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-8">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Venue Not Found</h2>
            <p className="text-gray-600 mb-4">The venue you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Venue Information</h1>
          <p className="text-gray-600">Manage and claim this venue</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Venue Information */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  {venue.venue_name}
                  <Badge variant={venue.is_claimed ? 'default' : 'secondary'}>
                    {venue.is_claimed ? 'Claimed' : 'Unclaimed'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-1 text-gray-500" />
                  <div>
                    <p className="font-medium">Address</p>
                    <p className="text-gray-600">
                      {venue.address_line && `${venue.address_line}, `}
                      {venue.city}, {venue.state_province} {venue.zip_code}
                    </p>
                    <p className="text-gray-600">{venue.country}</p>
                  </div>
                </div>

                {!venue.is_claimed && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">This venue is available for claiming!</h3>
                    <p className="text-blue-700 mb-3">
                      Take control of your venue's profile and start managing bookings, 
                      events, and customer interactions.
                    </p>
                    <Button onClick={handleClaimBusiness} className="w-full">
                      Claim This Business
                    </Button>
                  </div>
                )}

                {venue.is_claimed && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">This venue has been claimed</h3>
                    <p className="text-gray-600">
                      This venue is already managed by its owner. If you believe this is your venue, 
                      please contact support.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Claim Process Sidebar */}
          <div>
            {claimProcessStep === 'registration' && (
              <VenueRegistrationForm 
                venue={venue}
                onComplete={handleRegistrationComplete}
              />
            )}
            
            {claimProcessStep === 'payment' && (
              <VenuePaymentForm 
                venue={venue}
                onComplete={handlePaymentComplete}
              />
            )}
            
            {claimProcessStep === 'info' && (
              <Card>
                <CardHeader>
                  <CardTitle>Claim Benefits</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <span>Manage your venue profile</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <span>Accept and manage bookings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <span>Set your own rates and policies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <span>Access to analytics and insights</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Certification Agreements Modal */}
        <CertificationAgreementsModal
          open={showCertificationModal}
          roleType="venue"
          onSubmit={handleCertificationSubmit}
          onCancel={closeCertificationModal}
          isSubmitting={isCertSubmitting}
        />
      </div>
    </div>
  );
};

// Registration Form Component
const VenueRegistrationForm: React.FC<{
  venue: VenueProfile;
  onComplete: () => void;
}> = ({ venue, onComplete }) => {
  const [formData, setFormData] = useState({
    businessName: venue.venue_name,
    contactEmail: '',
    contactPhone: '',
    businessDescription: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Process registration
    toast.success('Registration information submitted');
    onComplete();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Registration Details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Business Name</Label>
            <Input 
              value={formData.businessName} 
              disabled 
              className="bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>
          
          <div>
            <Label>Contact Email *</Label>
            <Input 
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData(prev => ({...prev, contactEmail: e.target.value}))}
              required
            />
          </div>
          
          <div>
            <Label>Contact Phone *</Label>
            <Input 
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => setFormData(prev => ({...prev, contactPhone: e.target.value}))}
              required
            />
          </div>
          
          <div>
            <Label>Business Description</Label>
            <Input 
              value={formData.businessDescription}
              onChange={(e) => setFormData(prev => ({...prev, businessDescription: e.target.value}))}
              placeholder="Brief description of your venue"
            />
          </div>
          
          <Button type="submit" className="w-full">
            Continue to Payment
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Payment Form Component
const VenuePaymentForm: React.FC<{
  venue: VenueProfile;
  onComplete: () => void;
}> = ({ venue, onComplete }) => {
  const [processing, setProcessing] = useState(false);

  const handlePayment = async () => {
    setProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setProcessing(false);
      onComplete();
    }, 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold mb-2">Venue Claim Fee</h3>
          <p className="text-2xl font-bold text-blue-600">$99.99</p>
          <p className="text-sm text-gray-600 mt-1">One-time setup fee</p>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm font-medium">What's included:</p>
          <ul className="text-xs space-y-1 text-gray-600">
            <li>• Complete venue profile setup</li>
            <li>• Booking management system</li>
            <li>• Customer communication tools</li>
            <li>• Analytics and reporting</li>
            <li>• 24/7 support access</li>
          </ul>
        </div>
        
        <Button 
          onClick={handlePayment} 
          className="w-full"
          disabled={processing}
        >
          {processing ? 'Processing Payment...' : 'Pay $99.99'}
        </Button>
        
        <p className="text-xs text-gray-500 text-center">
          Secure payment processing. Your information is protected.
        </p>
      </CardContent>
    </Card>
  );
};

export default VenueClaim;